/**
 * ADHD triage decision model (frontend).
 *
 * Drives the four-step ADHD triage decision wizard (TD-176..179):
 *   Step 1 — Evidence of ADHD symptoms and impairment (SNAP surfacing + five
 *            single-select questions on one shared evidence scale)
 *   Step 2 — Other factors (grouped factor rows + risks sub-section)
 *   Step 3 — Your conclusion (free text + Accept Yes/No + clinicians involved)
 *   Step 4 — Signposting and recommendations
 *
 * The structured answers and surfaced data are decision support and inputs to
 * the letter-paragraph drafts; they never auto-compute or gate the decision —
 * the clinician's Accept / Not accepted choice is the decision.
 */

// ── Step 1: the shared evidence scale ────────────────────────────────────────
export const EVIDENCE_SCALE = [
  { id: 'no_evidence', label: 'No evidence' },
  { id: 'partial_evidence', label: 'Partial evidence' },
  { id: 'clear_evidence', label: 'Clear evidence' },
  { id: 'inconclusive', label: 'Inconclusive' },
];

export const EVIDENCE_SCALE_IDS = EVIDENCE_SCALE.map((s) => s.id);

/**
 * Step 1 questions. All five share EVIDENCE_SCALE and each supports an
 * optional free-text comment. `snapConsistency` renders directly under the
 * SNAP score display; the wizard handles that placement.
 */
export const EVIDENCE_QUESTIONS = [
  {
    key: 'settingsAcrossTwoOrMore',
    label:
      'Is there evidence of ADHD symptoms across two or more settings (e.g. home and school)?',
    supportsComment: true,
  },
  {
    key: 'onsetBeforeAge12',
    label: 'Were symptoms present before age 12?',
    supportsComment: true,
  },
  {
    key: 'functionalImpairment',
    label:
      'Is there evidence of functional impairment (school work, home, relationships)?',
    supportsComment: true,
  },
  {
    key: 'snapConsistency',
    label: 'Is there evidence that the SNAP scores are consistent across settings?',
    supportsComment: true,
  },
  {
    key: 'schoolReasonableAdjustments',
    label:
      'Has the school put reasonable adjustments in place that could be impacting the scores?',
    supportsComment: true,
  },
];

// ── Step 2: other-factor groups ──────────────────────────────────────────────
// Each factor row surfaces data where a clean source exists (`sourceKey` is a
// placeholder for that future auto-surfacing) and otherwise renders
// "Not captured — clinician to note" with a per-factor note affordance.
export const NOT_CAPTURED_LABEL = 'Not captured — clinician to note';

export const OTHER_FACTOR_GROUPS = [
  {
    id: 'medical',
    label: 'Medical & sensory',
    factors: [
      {
        factorKey: 'learningDisabilityCoOccurring',
        label: 'Learning disability / co-occurring conditions',
        sourceKey: null,
      },
      { factorKey: 'medications', label: 'Medications', sourceKey: null },
      {
        factorKey: 'speechLanguageCommunication',
        label: 'Speech, language & communication',
        sourceKey: null,
      },
      { factorKey: 'hearingOrVision', label: 'Hearing or vision', sourceKey: null },
      {
        factorKey: 'sensoryNeedBehaviour',
        label: 'Sensory-need-driven behaviour',
        sourceKey: null,
      },
    ],
  },
  {
    id: 'developmental',
    label: 'Developmental & cognitive',
    factors: [
      {
        factorKey: 'cognitiveFunctioning',
        label: 'Cognitive functioning / learning need',
        sourceKey: null,
      },
      { factorKey: 'developmentalDelay', label: 'Developmental delay', sourceKey: null },
    ],
  },
  {
    id: 'mentalHealth',
    label: 'Mental health & wellbeing',
    factors: [
      {
        factorKey: 'anxiety',
        label: 'Anxiety, including any support accessed',
        sourceKey: null,
      },
      {
        factorKey: 'trauma',
        label:
          'Trauma (domestic violence, bereavement, drugs or alcohol in the home, separation), including any support accessed',
        sourceKey: null,
      },
      {
        factorKey: 'attachmentDisorderSymptoms',
        label: 'Attachment disorder symptoms',
        sourceKey: null,
      },
    ],
  },
  {
    id: 'familyHome',
    label: 'Family, home & lifestyle',
    factors: [
      {
        factorKey: 'boundariesParenting',
        label: 'Boundaries / parenting (has a parenting intervention per NICE guidance been offered?)',
        sourceKey: null,
      },
      { factorKey: 'screenTimeGaming', label: 'Screen time / gaming', sourceKey: null },
      { factorKey: 'substanceMisuse', label: 'Substance misuse', sourceKey: null },
      {
        factorKey: 'homeLivingConditions',
        label: 'Home living conditions',
        sourceKey: null,
      },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    factors: [
      {
        factorKey: 'previousSupportAccessed',
        label: 'Support previously accessed',
        sourceKey: null,
      },
    ],
  },
];

export const OTHER_FACTOR_GROUP_IDS = OTHER_FACTOR_GROUPS.map((g) => g.id);

// The risks sub-section question (rendered with the read-only risk
// stratification surfacing in Step 2).
export const RISKS_QUESTION_LABEL =
  'Are there risks present that need reducing first, before an ADHD baseline can be assessed?';

// ── Step 3: conclusion helper prompts ────────────────────────────────────────
export const CONCLUSION_PROMPT =
  'Given all the information available, in your clinical judgement:';

export const CONCLUSION_HELPER_PROMPTS = [
  'Strengths observed',
  'Difficulties observed',
  'Is an ADHD assessment appropriate at this time, and why or why not?',
  'Recommended course of action',
];

export const ACCEPT_QUESTION_LABEL = 'Accept onto the ADHD assessment pathway?';

// Step 4 (signposting) is the pick-list in adhdSignposting.js / the backend's
// rms_signposting_options.py; the free-text prompt constants were retired with
// the Options4CYP list in place (TDI-230).

// ═════════════════════════════════════════════════════════════════════════════
// Legacy outcome model (historical records only)
// ─────────────────────────────────────────────────────────────────────────────
// The outcome-tree wizard this model drove has been replaced by the four-step
// wizard above. New ADHD decisions do not write `triageOutcomeCode`, but
// historical triageReviews entries keep theirs, so the option set below is
// retained purely so history/rendering surfaces can label old records.
// ═════════════════════════════════════════════════════════════════════════════

export const DECISION_ACCEPT = 'accept';
export const DECISION_REQUEST_INFO = 'request_info';
export const DECISION_CLOSE = 'close';

/** Legacy: evidence-checklist labels for historical `requiredItems` ids. */
export const REFERRAL_EVIDENCE_ITEMS = [
  { id: 'new_referral_form', label: 'a completed new referral form' },
  { id: 'snap_parent', label: 'the SNAP IV form completed by the parent/carer' },
  { id: 'snap_school', label: 'the SNAP IV form completed by the school/teacher' },
  { id: 'school_assessment', label: 'a school assessment form, including current learning levels' },
  { id: 'social_care_assessment', label: 'any relevant social care assessments' },
  { id: 'paediatric_letters', label: 'any relevant paediatric clinic letters' },
  { id: 'ehcp', label: 'the EHCP, if applicable' },
  { id: 'additional_questions', label: 'responses to the additional triage questions' },
];

/** Legacy: the retired 10-outcome option set (labels for historical codes). */
export const ADHD_OUTCOMES = [
  { id: 'accepted_waiting_list', number: 1, decision: DECISION_ACCEPT,
    label: 'Accepted — onto waiting list' },
  { id: 'accepted_private_on_medication', number: 7, decision: DECISION_ACCEPT,
    label: 'Private diagnosis, on medication' },
  { id: 'accepted_private_no_medication', number: 8, decision: DECISION_ACCEPT,
    label: 'Private diagnosis, no medication' },
  { id: 'info_requested_14_day', number: 3, decision: DECISION_REQUEST_INFO,
    label: 'Parts missing — 14 days to reply' },
  { id: 'closed_insufficient_information', number: 2, decision: DECISION_CLOSE,
    label: 'Lack of information' },
  { id: 'not_accepted_snap_below_threshold', number: 4, decision: DECISION_CLOSE,
    label: 'SNAP under clinical threshold' },
  { id: 'not_accepted_under_age', number: 5, decision: DECISION_CLOSE,
    label: 'Under 6/7' },
  { id: 'not_accepted_watchful_wait', number: 6, decision: DECISION_CLOSE,
    label: 'Period of watchful wait' },
  { id: 'not_accepted_trauma', number: 9, decision: DECISION_CLOSE,
    label: 'Trauma — baseline not possible' },
  { id: 'not_accepted_attachment_disorder', number: 10, decision: DECISION_CLOSE,
    label: 'Attachment disorder' },
];

/** Legacy: look up a historical outcome by its `triageOutcomeCode`. */
export function getOutcome(id) {
  return ADHD_OUTCOMES.find((o) => o.id === id) || null;
}
